import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LegalFooter from '../../components/legal/LegalFooter';
import { Sparkles } from 'lucide-react';

/**
 * Privacy Policy — written specifically for Mife (mifelife.com), an EU-based
 * B2B sales-intelligence and personal-productivity SaaS that processes user
 * input, OAuth tokens (planned), AI-generated content, and B2B contact data
 * sourced from publicly available business directories.
 *
 * This page is intentionally locale-aware: TR by default, EN & DE supported.
 */
export default function PrivacyPage() {
  const { pathname } = useLocation();
  const lang = pathname.startsWith('/en/') || pathname === '/en' ? 'en'
    : pathname.startsWith('/de/') || pathname === '/de' ? 'de'
    : 'tr';
  const t = COPY[lang];

  useEffect(() => {
    document.title = `${t.h1} · Mife`;
    document.documentElement.setAttribute('lang', lang);
  }, [lang, t.h1]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold" data-testid="privacy-logo">
            <Sparkles className="w-5 h-5 text-blue-600" /> Mife
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="privacy-back">
            {t.back}
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-sm sm:prose-base prose-slate max-w-none" data-testid="privacy-page">
        <h1 data-testid="privacy-h1">{t.h1}</h1>
        <p className="text-muted-foreground" data-testid="privacy-updated">{t.updated}</p>
        {t.sections.map((s, i) => (
          <section key={i} data-testid={`privacy-section-${i}`}>
            <h2>{s.h}</h2>
            {s.p.map((para, j) => <p key={j}>{para}</p>)}
            {s.ul && (
              <ul>
                {s.ul.map((li, k) => <li key={k}>{li}</li>)}
              </ul>
            )}
          </section>
        ))}
      </main>
      <LegalFooter lang={lang} />
    </div>
  );
}

const COPY = {
  tr: {
    h1: 'Gizlilik Politikası',
    updated: 'Son güncelleme: 5 Mart 2026',
    back: '← Ana sayfa',
    sections: [
      {
        h: '1. Veri sorumlusu',
        p: ['Mife (mifelife.com), bu hizmeti sağlayan ve aşağıda anlatılan veri işleme faaliyetinden sorumlu taraftır. İletişim için: hello@mifelife.com.'],
      },
      {
        h: '2. Topladığımız veriler',
        p: ['Hizmeti sunmak için aşağıdaki veri kategorilerini topluyoruz:'],
        ul: [
          'Hesap bilgileri: ad, e-posta, hashlenmiş şifre, dil ve ürün tercihi (sales/life).',
          'Kullanım: girdiğin Sales profili (ne sattığın, hedef sektör/ülke), oluşturduğun AI mailler, açtığın leadler.',
          'Teknik: IP, cihaz/tarayıcı ve oturum çerezleri (sadece auth amacıyla).',
          'B2B lead veri tabanı: Apollo benzeri kamuya açık ticari rehberlerden derlenmiş şirket isimleri, iş e-postaları, unvanlar. Tüketici verisi değildir.',
          'Ödeme: iyzico üzerinden işlenir. Mife kart numaranı saklamaz.',
        ],
      },
      {
        h: '3. Hangi amaçla ve hangi yasal dayanakla',
        p: ['Verileri (a) sözleşmenin ifası (KVKK 5/2-c, GDPR 6(1)(b)), (b) meşru menfaat (KVKK 5/2-f, GDPR 6(1)(f)) veya (c) açık rıza temelinde işliyoruz. AI mail üretiminde girdilerin OpenAI’ye iletilir; bu işleme GDPR Madde 28 kapsamında bir veri işleyici sözleşmesi (DPA) çerçevesindedir.'],
      },
      {
        h: '4. Üçüncü taraf işleyiciler',
        p: ['Veri akışını şeffaf tutuyoruz:'],
        ul: [
          'OpenAI (GPT-5.2) — AI metin üretimi (ABD; SCC + DPA)',
          'MongoDB Atlas — veri saklama (AB-içi region)',
          'iyzico — ödeme işleme (TR)',
          'Resend — transactional e-postalar (AB)',
          'Cloudflare — CDN ve güvenlik (global edge)',
        ],
      },
      {
        h: '5. Saklama süreleri',
        p: ['Hesap aktif olduğu sürece + hesap silindikten 30 gün sonra. Ödeme makbuzları yasal yükümlülük gereği 10 yıl saklanır.'],
      },
      {
        h: '6. Haklarınız',
        p: ['KVKK ve GDPR kapsamında erişim, düzeltme, silme, işlemeyi kısıtlama, taşıma, itiraz ve denetim makamına şikayet hakkınız vardır. Hesabınızı her zaman ayarlardan silebilir veya hello@mifelife.com adresinden talep iletebilirsiniz.'],
      },
      {
        h: '7. Çerezler',
        p: ['Yalnızca oturum yönetimi için zorunlu çerezler kullanıyoruz. Reklam veya üçüncü taraf izleme çerezi yoktur.'],
      },
      {
        h: '8. Çocukların gizliliği',
        p: ['Mife, 16 yaş altı kullanıcılara hizmet vermez ve bilerek bu yaş grubundan veri toplamaz.'],
      },
      {
        h: '9. Değişiklikler',
        p: ['Bu politikada önemli bir değişiklik olduğunda sizi e-posta veya uygulama içi bildirim ile bilgilendireceğiz.'],
      },
    ],
  },
  en: {
    h1: 'Privacy Policy',
    updated: 'Last updated: March 5, 2026',
    back: '← Home',
    sections: [
      { h: '1. Data controller', p: ['Mife (mifelife.com) is the controller for the personal data described below. Contact: hello@mifelife.com.'] },
      { h: '2. What we collect', p: ['To deliver the service we collect:'], ul: [
        'Account: name, email, hashed password, language and selected product (sales/life).',
        'Usage: the Sales profile you fill in, AI emails you generate, and leads you unlock.',
        'Technical: IP address, device/browser metadata, and session cookies (auth-only).',
        'B2B database: company names, business emails and titles compiled from publicly available business directories — not consumer data.',
        'Payment: processed by iyzico. Mife never stores your card number.',
      ] },
      { h: '3. Purpose and legal basis', p: ['We process data on (a) contract performance (GDPR 6(1)(b)), (b) legitimate interest (GDPR 6(1)(f)), or (c) explicit consent. AI generations call OpenAI under a GDPR Article 28 DPA.'] },
      { h: '4. Sub-processors', p: ['We keep data flow transparent:'], ul: [
        'OpenAI (GPT-5.2) — AI text generation (US; SCCs + DPA)',
        'MongoDB Atlas — storage (EU region)',
        'iyzico — payment processing (TR)',
        'Resend — transactional email (EU)',
        'Cloudflare — CDN & security (global edge)',
      ] },
      { h: '5. Retention', p: ['For as long as your account is active + 30 days after deletion. Payment receipts are retained 10 years by tax law.'] },
      { h: '6. Your rights', p: ['Under GDPR you have rights of access, rectification, erasure, restriction, portability, objection, and to lodge a complaint with a supervisory authority. Delete your account anytime from Settings or write to hello@mifelife.com.'] },
      { h: '7. Cookies', p: ['We only set strictly necessary cookies for session/auth. No advertising or third-party tracking cookies.'] },
      { h: '8. Children', p: ['Mife is not directed at users under 16 and we do not knowingly collect their data.'] },
      { h: '9. Changes', p: ['We will notify you of material changes by email or in-app banner.'] },
    ],
  },
  de: {
    h1: 'Datenschutzerklärung',
    updated: 'Zuletzt aktualisiert: 5. März 2026',
    back: '← Startseite',
    sections: [
      { h: '1. Verantwortlicher', p: ['Mife (mifelife.com) ist Verantwortlicher für die unten beschriebene Datenverarbeitung. Kontakt: hello@mifelife.com.'] },
      { h: '2. Welche Daten wir erheben', p: ['Zur Erbringung des Dienstes erheben wir:'], ul: [
        'Konto: Name, E-Mail, gehashtes Passwort, Sprache und ausgewähltes Produkt (sales/life).',
        'Nutzung: Sales-Profil-Eingaben, generierte KI-E-Mails, entsperrte Leads.',
        'Technisch: IP, Gerät/Browser, Session-Cookies (nur Auth).',
        'B2B-Datenbank: Firmennamen, geschäftliche E-Mails, Titel aus öffentlich zugänglichen Branchenverzeichnissen — keine Verbraucherdaten.',
        'Zahlung: über iyzico. Mife speichert keine Kartennummern.',
      ] },
      { h: '3. Zweck und Rechtsgrundlage', p: ['Wir verarbeiten Daten auf Basis von (a) Vertragserfüllung (DSGVO 6(1)(b)), (b) berechtigtem Interesse (DSGVO 6(1)(f)) oder (c) Einwilligung. KI-Generierungen erfolgen über OpenAI unter einem AVV nach Art. 28 DSGVO.'] },
      { h: '4. Auftragsverarbeiter', p: ['Wir halten den Datenfluss transparent:'], ul: [
        'OpenAI (GPT-5.2) — KI-Textgenerierung (US; SCCs + AVV)',
        'MongoDB Atlas — Speicherung (EU-Region)',
        'iyzico — Zahlungsabwicklung (TR)',
        'Resend — Transactional E-Mail (EU)',
        'Cloudflare — CDN & Sicherheit (globaler Edge)',
      ] },
      { h: '5. Speicherdauer', p: ['Solange dein Konto aktiv ist + 30 Tage nach Löschung. Zahlungsbelege werden gemäß Abgabenordnung 10 Jahre aufbewahrt.'] },
      { h: '6. Deine Rechte', p: ['Du hast nach DSGVO Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung, Übertragbarkeit, Widerspruch und Beschwerde bei der Aufsichtsbehörde. Lösche dein Konto jederzeit in den Einstellungen oder schreibe an hello@mifelife.com.'] },
      { h: '7. Cookies', p: ['Nur unbedingt erforderliche Session-/Auth-Cookies. Keine Werbe- oder Tracking-Cookies.'] },
      { h: '8. Kinder', p: ['Mife richtet sich nicht an Nutzer unter 16 und sammelt nicht wissentlich deren Daten.'] },
      { h: '9. Änderungen', p: ['Wesentliche Änderungen kommunizieren wir per E-Mail oder In-App-Banner.'] },
    ],
  },
};
